"use client";

import React from "react";
import { Search, Map, Cpu, TrendingUp, Type } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const stages = [{
  title: "Discovery & Strategic Assessment",
  description: "We begin by analyzing your organization’s technology landscape, operational workflows, and business objectives. This allows us to identify opportunities where AI, cloud architecture, and automation can create the greatest impact.",
  icon: Search
}, {
  title: "AI & Cloud Strategy Design",
  description: "Next, we develop a custom technology roadmap tailored to your organization. Our team designs scalable frameworks for AI adoption, cloud transformation, and intelligent automation that align with long-term business goals.",
  icon: Map
}, {
  title: "Implementation & System Modernization",
  description: "We work alongside your internal teams to deploy solutions, modernize legacy systems, and integrate new technologies into existing operations. Our approach ensures minimal disruption while maximizing operational efficiency.",
  icon: Cpu
}, {
  title: "Optimization & Measurable Results",
  description: "Digital transformation is an ongoing process. We continuously monitor performance, refine automation strategies, and measure results against key business metrics to ensure sustained growth and competitive advantage.",
  icon: TrendingUp
}];
function AnimatedStageCard({
  stage,
  index
}: {
  stage: {
    title: string;
    description: string;
    icon: LucideIcon;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px"
  });
  const Icon = stage.icon;
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-shadow duration-300">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start mb-6">
            <span className="font-mono text-secondary font-bold text-3xl tracking-tighter">
              0{index + 1}
            </span>
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon className="w-7 h-7 text-accent-foreground stroke-[1.5]" />
            </div>
          </div>
          <CardTitle className="text-xl font-semibold text-foreground leading-tight">
            {stage.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">
            {stage.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function OurApproach() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section id="ourapproach" className="py-[120px] bg-background overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-foreground tracking-tight mb-6">
            Our Approach to Digital Transformation
          </h2>
          <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed">At Cyril Consulting Group, we combine strategic insight with technical expertise to guide organizations through every stage of their digital transformation journey. Our methodology ensures that artificial intelligence, cloud infrastructure, and automation initiatives deliver measurable business value.</p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stages.map((stage, index) => <AnimatedStageCard key={index} stage={stage} index={index} />)}
        </div>

      </div>
    </section>;
}